import {
  Select,
  FormFeedBack,
  removeTelephoneCountryCode,
  convertEventSelectToTarget,
  STATUSPAGE,
} from '@repo/ui';
import { CpfInput, TelPhone } from 'adasi_components';
import {
  GetByIdProfileUseCaseInterface,
  UpdateProfileUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { useAuth } from 'presentation/app/hooks/useAuth';
import { sexOptions } from 'presentation/app/modules/Entities/models/sex-options';
import { addCpfMask } from 'presentation/app/utils/cpf/masks';
import {
  Row,
  Col,
  Form,
  Input,
  Label,
  Button,
  CardBody,
  CardFooter,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';

interface IProps {
  requestUpdateProfile: (params: UpdateProfileUseCaseInterface.Params) => void;
  profileList: GetByIdProfileUseCaseInterface.Result;
  loadingUpdateProfile: STATUSPAGE;
}

export const AccountDetails = ({
  profileList,
  requestUpdateProfile,
  loadingUpdateProfile,
}: IProps) => {
  // hooks
  const { currentUser } = useAuth();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      cpf: profileList ? addCpfMask(profileList.cpf) : '',
      name: profileList ? profileList.name : '',
      sexo: profileList?.gender
        ? {
            label: profileList.gender === 'M' ? 'Masculino' : 'Feminino',
            value: profileList.gender,
          }
        : null,
      email: profileList ? profileList.email : '',
      phone: profileList?.phone ? profileList?.phone : null,
      birthDate: profileList?.birthDate
        ? profileList.birthDate.split('T')[0]
        : '',
    },

    validationSchema: Yup.object().shape({
      cpf: Yup.string().required('CPF é obrigatório').cpfType('CPF é inválido'),
      name: Yup.string().required('Nome é obrigatório'),
      email: Yup.string()
        .required('Email é obrigatório')
        .email('Email é inválido'),
      sexo: Yup.object()
        .shape({
          label: Yup.string().required('Sexo é obrigatório'),
          value: Yup.string().required('Sexo é obrigatório'),
        })
        .required('Sexo é obrigatório'),

      phone: Yup.string()
        .required('Telefone é obrigatório')
        .min(10, 'Telefone é inválido'),
      birthDate: Yup.string().required('Data de nascimento é obrigatório'),
    }),

    onSubmit: (values) => {
      requestUpdateProfile({
        id: currentUser.id,
        email: values.email,
        gender: values.sexo.value,
        birthdate: values.birthDate,
        phone: removeTelephoneCountryCode(values.phone),
      });
    },
  });
  console.log('validation', validation.values?.phone);

  return (
    <Form
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        validation.handleSubmit();
        return false;
      }}
    >
      <CardBody>
        <Row>
          <Col md={4}>
            <Label htmlFor="cpf" className="form-label">
              CPF
            </Label>
            <CpfInput
              tabIndex={1}
              name="cpf"
              onBlur={validation.handleBlur}
              disabled={true}
              inputId="cpf"
              className="form-control"
              value={validation.values.cpf}
              handleChange={validation.handleChange}
              invalid={!!(validation.touched.cpf && validation.errors.cpf)}
            />
            <FormFeedBack
              error={validation?.errors?.cpf}
              isInvalid={
                !!(validation?.touched?.cpf && validation?.errors?.cpf)
              }
            />
          </Col>
          <Col md={4}>
            <Label htmlFor="name" className="form-label">
              Nome
            </Label>
            <Input
              name="name"
              type="text"
              className="form-control"
              disabled={true}
              value={validation.values.name}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.name && validation.errors.name)}
            />
            <FormFeedBack
              error={validation.errors?.name}
              isInvalid={!!(validation.touched.name && validation.errors?.name)}
            />
          </Col>
          <Col md={4}>
            <Label htmlFor="email" className="form-label">
              Email
            </Label>
            <Input
              name="email"
              type="email"
              className="form-control"
              value={validation.values.email}
              onChange={validation.handleChange}
              invalid={!!(validation.touched.email && validation.errors.email)}
            />
            <FormFeedBack
              error={validation.errors?.email}
              isInvalid={
                !!(validation.touched.email && validation.errors?.email)
              }
            />
          </Col>
          <Col md={4} className="mt-2">
            <Label htmlFor="birthDate" className="form-label">
              Data de Nascimento
            </Label>
            <Input
              name="birthDate"
              type="date"
              className="form-control"
              value={validation.values.birthDate}
              onChange={validation.handleChange}
              invalid={
                !!(validation.touched.birthDate && validation.errors.birthDate)
              }
            />
            <FormFeedBack
              error={validation.errors?.birthDate}
              isInvalid={
                !!(validation.touched.birthDate && validation.errors?.birthDate)
              }
            />
          </Col>
          <Col md={4}>
            <Label htmlFor="sexo" className="form-label mt-2">
              Sexo
            </Label>
            <Select
              id="sexo"
              name="sexo"
              placeholder=""
              onBlur={validation.handleBlur}
              value={validation?.values?.sexo as any}
              options={sexOptions}
              onChange={(e) => {
                validation.handleChange(convertEventSelectToTarget('sexo', e));
              }}
              invalid={
                !!(validation?.touched?.sexo && validation?.errors?.sexo)
              }
            />

            <FormFeedBack
              error={validation.errors?.sexo as any}
              isInvalid={!!(validation.touched.sexo && validation.errors?.sexo)}
            />
          </Col>

          <Col className="mt-2" md={4}>
            <Label htmlFor="phone">Telefone</Label>
            <TelPhone
              id="phone"
              name="phone"
              value={`+55${validation.values.phone}`}
              onChange={(e: string) => {
                const inputValue = {
                  target: {
                    name: 'phone',
                    value: removeTelephoneCountryCode(e),
                  },
                };
                validation.handleChange(inputValue);
              }}
              onBlur={validation.handleBlur}
              invalid={!!(validation.touched.phone && validation.errors?.phone)}
            />
            {validation.touched.phone && validation.errors?.phone ? (
              <FormFeedBack
                error={validation?.errors?.phone?.toString()}
                isInvalid={
                  !!(validation?.touched.phone && validation?.errors?.phone)
                }
              />
            ) : null}
          </Col>
        </Row>
      </CardBody>

      <CardFooter className="d-flex justify-content-end align-content-end">
        {/* <button className="btn btn-link">Cancelar</button> */}

        <Button
          type="submit"
          disabled={loadingUpdateProfile === STATUSPAGE.LOADING}
          color="primary"
        >
          Atualizar
          {loadingUpdateProfile === STATUSPAGE.LOADING && (
            <Spinner className="ms-2" size="sm">
              {['Carregando...']}
            </Spinner>
          )}
        </Button>
      </CardFooter>
    </Form>
  );
};
